import React, { useContext, useState } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import useGmailAuth from '../hooks/useGmailAuth';
import { useSettings } from '../hooks/useSettings';
import DisconnectGmailModal from '../components/DisconnectGmailModal';



const Settings = () => {
  const { currentUser } = useContext(AuthContext);
  const {
    googleConnected,
    googleEmail,
    gmailSyncEnabled,
    calendarSyncEnabled,
    loading: authLoading,
    connecting,
    disconnecting,
    connectGoogle,
    disconnectGoogle
  } = useGmailAuth(currentUser);

  const {
    settings,
    loading: settingsLoading,
    updatePipeline
  } = useSettings();
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const handleDisconnectClick = () => {
    setShowDisconnectModal(true);
  };

  const handleDisconnectConfirm = async () => {
    await disconnectGoogle();
    setShowDisconnectModal(false);
  };

  const handleDisconnectCancel = () => {
    setShowDisconnectModal(false);
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-page relative">
      {/* Header */}
      <div className="shrink-0 px-6 py-5 border-b border-neutral-200 bg-white/80 backdrop-blur-sm z-10">
        <div className="max-w-[900px] mx-auto w-full">
          <h1 className="font-display text-3xl text-neutral-900 tracking-tight">
            Settings
          </h1>
          <p className="text-neutral-500 text-sm mt-1">
            Connect your favorite tools to sync emails, calendar, and automations.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
        <div className="max-w-[900px] mx-auto w-full flex flex-col gap-8">

          {/* Google Workspace Section */}
          <section className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                <span className="material-symbols-outlined text-neutral-400">grid_view</span>
                Google Services
              </h2>
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-success-light border border-success/20">
                <span className="material-symbols-outlined text-success text-[16px]">verified_user</span>
                <span className="text-xs font-medium text-success">Encrypted Data</span>
              </div>
            </div>

            {/* Gmail & Calendar Unified Card */}
            <div className="flex flex-col p-6 rounded-xl border border-neutral-200 bg-white shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="size-12 rounded-lg bg-red-50 flex items-center justify-center border border-red-100">
                    <span className="material-symbols-outlined text-[#EA4335] text-2xl">mail</span>
                  </div>
                  <div className="size-12 rounded-lg bg-blue-50 flex items-center justify-center border border-blue-100">
                    <span className="material-symbols-outlined text-[#4285F4] text-2xl">calendar_month</span>
                  </div>
                </div>
                {authLoading ? (
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-500 border border-neutral-200">
                    <span className="material-symbols-outlined animate-spin text-[12px]">progress_activity</span>
                    Loading...
                  </div>
                ) : googleConnected ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-success-light text-success border border-success/20">
                    <span className="size-1.5 rounded-full bg-success"></span>
                    Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-500 border border-neutral-200">
                    <span className="size-1.5 rounded-full bg-neutral-400"></span>
                    Not Connected
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-1 mb-6">
                <h3 className="text-neutral-900 text-lg font-semibold">Gmail & Calendar</h3>
                <p className="text-neutral-500 text-sm">Automatically sync emails and calendar events with your CRM contacts and deals.</p>
                {googleConnected && googleEmail && (
                  <div className="flex flex-col gap-2 mt-3">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200">
                      <span className="material-symbols-outlined text-neutral-400 text-[16px]">account_circle</span>
                      <span className="text-sm text-neutral-700 font-medium">{googleEmail}</span>
                    </div>
                    <div className="flex items-center gap-4 px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-neutral-400 text-[16px]">mail</span>
                        <span className="text-xs text-neutral-500">Gmail:</span>
                        <span className={`text-xs font-medium ${gmailSyncEnabled ? 'text-success' : 'text-neutral-400'}`}>
                          {gmailSyncEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-neutral-400 text-[16px]">calendar_month</span>
                        <span className="text-xs text-neutral-500">Calendar:</span>
                        <span className={`text-xs font-medium ${calendarSyncEnabled ? 'text-success' : 'text-neutral-400'}`}>
                          {calendarSyncEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-auto">
                {googleConnected ? (
                  <button
                    onClick={handleDisconnectClick}
                    disabled={disconnecting}
                    className="w-full py-2.5 px-4 rounded-lg bg-neutral-100 text-neutral-600 hover:bg-neutral-200 hover:text-neutral-800 font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {disconnecting ? (
                      <>
                        <span className="material-symbols-outlined animate-spin text-[16px]">progress_activity</span>
                        Disconnecting...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-[16px]">link_off</span>
                        Disconnect
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={connectGoogle}
                    disabled={connecting || authLoading}
                    className="w-full py-2.5 px-4 rounded-lg bg-primary text-white font-semibold text-sm hover:bg-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md shadow-primary/25"
                  >
                    {connecting ? (
                      <>
                        <span className="material-symbols-outlined animate-spin text-[16px]">progress_activity</span>
                        Connecting...
                      </>
                    ) : (
                      'Connect Account'
                    )}
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* Pipeline Settings Section */}
          <section className="flex flex-col gap-4">
            <h2 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
              <span className="material-symbols-outlined text-neutral-400">bar_chart</span>
              Pipeline Settings
            </h2>

            <div className="rounded-xl border border-neutral-200 bg-white shadow-sm overflow-hidden p-6">
              {settingsLoading ? (
                <div className="flex items-center gap-2 text-neutral-500">
                  <span className="material-symbols-outlined animate-spin text-[16px]">progress_activity</span>
                  Loading pipeline...
                </div>
              ) : settings?.pipelines?.length > 0 ? (
                <div className="flex flex-col gap-6">
                  {settings.pipelines.map((pipeline) => (
                    <div key={pipeline.id} className="flex flex-col gap-4">
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                          Pipeline Name
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            defaultValue={pipeline.name}
                            onBlur={(e) => {
                              if (e.target.value !== pipeline.name) {
                                updatePipeline(pipeline.id, { name: e.target.value });
                              }
                            }}
                            className="flex-1 px-4 py-2.5 rounded-lg bg-neutral-50 border border-neutral-200 text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary transition-all"
                          />
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                          Stages
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                          {pipeline.stages?.map((stage) => (
                            <div key={stage.position} className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50 border border-neutral-200">
                              <div className="size-3 rounded-full" style={{ backgroundColor: stage.color }}></div>
                              <span className="text-sm font-medium text-neutral-700">{stage.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-neutral-500 text-sm">No pipelines found.</div>
              )}
            </div>
          </section>

          {/* Automation Section */}
          <section className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                <span className="material-symbols-outlined text-neutral-400">bolt</span>
                Automation
              </h2>
              <button className="text-sm text-primary hover:text-primary-500 font-medium transition-colors">
                API Documentation
              </button>
            </div>

            {/* Webhook Configuration Panel */}
            <div className="rounded-xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
              <div className="p-6 flex flex-col lg:flex-row gap-6 lg:items-start">
                <div className="flex-1 flex flex-col gap-2">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
                      <span className="material-symbols-outlined text-indigo-500">webhook</span>
                    </div>
                    <h3 className="text-lg font-semibold text-neutral-900">Webhooks</h3>
                  </div>
                  <p className="text-neutral-500 text-sm leading-relaxed">
                    Send real-time HTTP notifications to external systems when specific events occur in the CRM (e.g., New Lead, Deal Won).
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button className="px-4 py-2 rounded-lg bg-primary hover:bg-primary-500 text-white text-sm font-medium transition-colors shadow-md shadow-primary/25 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[18px]">add</span>
                    New Webhook
                  </button>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* Disconnect Gmail Modal */}
      <DisconnectGmailModal
        isOpen={showDisconnectModal}
        onClose={handleDisconnectCancel}
        onConfirm={handleDisconnectConfirm}
        isDisconnecting={disconnecting}
        gmailEmail={googleEmail}
      />
    </div>
  );
};

export default Settings;
